import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AuthLayout from '../components/AuthLayout';
import userService, { User } from '../services/user.service';
import { useAuth } from '../contexts/AuthContext';
import LoadingIndicator from '../components/LoadingIndicator';
import ErrorFallback from '../components/ErrorFallback';
import EmptyState from '../components/EmptyState';

// Mapeamento de perfis para exibição em português
const roleLabels: Record<string, string> = {
  'admin': 'Administrador',
  'manager': 'Comercial',
  'field-agent': 'Técnico',
  'user': 'Usuário'
};

// Cores para os chips de perfil
const roleColors: Record<string, string> = {
  'admin': 'error',
  'manager': 'primary',
  'field-agent': 'success',
  'user': 'default'
};

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Carregar usuários
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    console.log('Buscando usuários...');
    try {
      const data = await userService.getUsers();
      console.log('Usuários recebidos:', data);
      setUsers(data || []);
      setFilteredUsers(data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar usuários');
      console.error('Erro ao carregar usuários:', err);
      // Definir listas vazias para evitar erros de renderização
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filtrar usuários quando o termo de busca mudar
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = users.filter(
        user =>
          user.displayName?.toLowerCase().includes(lowercasedSearch) ||
          user.email.toLowerCase().includes(lowercasedSearch) ||
          roleLabels[user.role].toLowerCase().includes(lowercasedSearch)
      );
      setFilteredUsers(filtered);
    }
    setPage(0);
  }, [searchTerm, users]);

  // Manipuladores de paginação
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Manipuladores de diálogo de exclusão
  const handleOpenDeleteDialog = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Desativar usuário
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await userService.deleteUser(userToDelete._id!);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      // Atualizar a lista após a exclusão
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao desativar usuário');
      console.error('Erro ao desativar usuário:', err);
    }
  };

  return (
    <AuthLayout title="Lista de Usuários">
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Usuários</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/users/new')}
          >
            Novo Usuário
          </Button>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <TextField
              placeholder="Buscar usuários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
            />
          </Box>

          {loading ? (
            <LoadingIndicator message="Carregando usuários..." />
          ) : error ? (
            <ErrorFallback 
              error={error} 
              resetErrorBoundary={fetchUsers} 
              message="Ocorreu um erro ao carregar os usuários." 
            />
          ) : filteredUsers.length === 0 ? (
            <EmptyState 
              title="Nenhum usuário encontrado" 
              message="Não há usuários cadastrados ou correspondentes ao filtro aplicado." 
              actionText="Criar novo usuário" 
              onAction={() => navigate('/users/new')} 
            />
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Perfil</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((user) => (
                        <TableRow key={user._id} hover>
                          <TableCell>{user.displayName || 'Sem nome'}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Chip 
                              label={roleLabels[user.role] || user.role} 
                              color={roleColors[user.role] as any || 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.isActive ? 'Ativo' : 'Inativo'}
                              color={user.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton 
                              color="info" 
                              onClick={() => navigate(`/users/${user._id}`)}
                              size="small"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              color="primary" 
                              onClick={() => navigate(`/users/edit/${user._id}`)}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              color="error" 
                              onClick={() => handleOpenDeleteDialog(user)}
                              size="small"
                              disabled={user.firebaseUid === currentUser?.uid}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredUsers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
              />
            </>
          )}
        </Paper>
      </Box>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Desativar Usuário</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja desativar o usuário {userToDelete?.displayName || userToDelete?.email}?
            Esta ação não exclui o usuário do sistema, apenas o marca como inativo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteUser} color="error">
            Desativar
          </Button>
        </DialogActions>
      </Dialog>
    </AuthLayout>
  );
};

export default UserList;
